import { BrowserModule } from "@angular/platform-browser";
import { NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { AngularTokenModule } from "angular-token";
import { AuthInterceptor } from "./interceptors/auth.interceptor";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { RouterModule } from "@angular/router";
import { environment } from "../environments/environment";
import { ToastrModule } from "ngx-toastr";
import { SharedModule } from "./shared/shared.module";
import { AuthenticationModule } from "./authentication/authentication.module";
import { OnboardingModule } from "./onboarding/onboarding.module";
import { AdminModule } from "./admin/admin.module";
import { LoaderService } from "./utils/loader.service";
import { PageNotfoundComponent } from "./page-notfound/page-notfound.component";
import { AutocompleteLibModule } from "angular-ng-autocomplete";
import { NgxPaginationModule } from "ngx-pagination";
import { NgMultiSelectDropDownModule } from "ng-multiselect-dropdown";
import { NgSelectModule } from "@ng-select/ng-select";
import { SwitchPortalComponent } from "./switch-portal/switch-portal.component";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatDialogModule } from "@angular/material/dialog";
import { ReviewBiddingComponent } from "./admin/shared/pop-ups/review-bidding/review-bidding.component";
import { BiddingFinalConfirmationComponent } from "./admin/shared/pop-ups/bidding-final-confirmation/bidding-final-confirmation.component";
import { MatSelectModule } from "@angular/material/select";
import { MatRadioModule } from "@angular/material/radio";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatTableModule } from "@angular/material/table";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatMenuModule } from "@angular/material/menu";
import { MatSortModule } from "@angular/material/sort";
import { MatInputModule } from "@angular/material/input";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatStepperModule } from "@angular/material/stepper";
import { PartLevelDetailsComponent } from "./admin/shared/pop-ups/part-level-details/part-level-details.component";
import { RejectLiveRfqComponent } from "./admin/shared/pop-ups/reject-live-rfq/reject-live-rfq.component";
import { RejectPoComponent } from "./admin/shared/pop-ups/reject-po/reject-po.component";
import { AcceptPurchaseOrdersComponent } from "./admin/shared/pop-ups/accept-purchase-orders/accept-purchase-orders.component";
import { PoReadyToShipComponent } from "./admin/shared/pop-ups/po-ready-to-ship/po-ready-to-ship.component";
import { FillPartLevelCostComponent } from "./admin/shared/pop-ups/fill-part-level-cost/fill-part-level-cost.component";
import { MoveToInventoryComponent } from "./admin/shared/pop-ups/move-to-inventory/move-to-inventory.component";
import { SheetMetalComponent } from "./onboarding/machine-popups/sheet-metal/sheet-metal.component";
import { SubmitOtpComponent } from "./onboarding/machine-popups/submit-otp/submit-otp.component";
import { OtherMachinesComponent } from "./onboarding/machine-popups/other-machines/other-machines.component";
import { FileUploadModule } from "@iplab/ngx-file-upload";
import { NewPrintProcessComponent } from "./onboarding/machine-popups/new-print-process/new-print-process.component";
import { NewIndustryComponent } from "./onboarding/machine-popups/new-industry/new-industry.component";
import { NewCertificatesComponent } from "./onboarding/machine-popups/new-certificates/new-certificates.component";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { FormsModule } from "@angular/forms";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { CalculateAnalyzer } from "./calculate/calculate-analyzer.component";
import { AvatarModule, AvatarSource } from "ngx-avatar";
import { MatButtonModule } from "@angular/material/button";
import { SubmitCostingComponent } from "./admin/shared/pop-ups/submit-costing/submit-costing.component";
import { ChatAgreementComponent } from "./admin/shared/pop-ups/chat-agreement/chat-agreement.component";
import { NgxAiChatModule } from "ngx-ai-chat";
const avatarSourcesOrder = [AvatarSource.CUSTOM, AvatarSource.INITIALS];

@NgModule({
  declarations: [
    AppComponent,
    PageNotfoundComponent,
    SwitchPortalComponent,
    CalculateAnalyzer,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    BrowserAnimationsModule,
    HttpClientModule,
    RouterModule,
    FormsModule,
    NgxPaginationModule,
    NgMultiSelectDropDownModule.forRoot(),
    NgSelectModule,
    ToastrModule.forRoot({ closeButton: true, timeOut: 3000 }),
    SharedModule,
    AngularTokenModule.forRoot({
      apiBase: environment.apiBaseUrl,
      resetPasswordCallback: environment.uiBaseUrl + "/reset-password",
      registerAccountCallback: environment.uiBaseUrl + "/login",
    }),
    OnboardingModule,
    AuthenticationModule,
    AdminModule,
    AppRoutingModule,
    AutocompleteLibModule,
    MatAutocompleteModule,
    MatDialogModule,
    MatSelectModule,
    MatRadioModule,
    MatFormFieldModule,
    MatExpansionModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatInputModule,
    MatSortModule,
    MatTooltipModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatStepperModule,
    FileUploadModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    AvatarModule.forRoot({
      sourcePriorityOrder: avatarSourcesOrder,
    }),
    NgxAiChatModule
  ],
  providers: [
    AngularTokenModule,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    LoaderService,
  ],
  bootstrap: [AppComponent],
  entryComponents: [
    ReviewBiddingComponent,
    BiddingFinalConfirmationComponent,
    PartLevelDetailsComponent,
    RejectLiveRfqComponent,
    RejectPoComponent,
    AcceptPurchaseOrdersComponent,
    PoReadyToShipComponent,
    FillPartLevelCostComponent,
    MoveToInventoryComponent,
    SheetMetalComponent,
    SubmitOtpComponent,
    OtherMachinesComponent,
    NewPrintProcessComponent,
    NewIndustryComponent,
    NewCertificatesComponent,
    SubmitCostingComponent,
    ChatAgreementComponent
  ],
})
export class AppModule {}
